import type { Skill } from '../skillsData';

// Supplemental exchanges skills from skills/exchanges/ directory

export const cryptoskillExchangesExtraData: Skill[] = [

  // ── Aegis ──
  {
    id: 'aegis-exchange',
    name: 'aegis',
    author: 'Aegis Protocol',
    authorHandle: '@aegis_protocol',
    tag: 'Skill',
    category: 'trading',
    description: 'Aegis exchange skill. Risk management and smart order routing layer on top of major CEXes to guard against liquidations and adverse fills.',
    downloads: 12400,
    stars: 620,
    version: '1.0.0',
    installCommand: '> install the aegis skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/aegis',
    usageExample: '> enable aegis guard\n> set liquidation fence\n> route smart order',
    detailedDescription: 'Aegis exchange risk layer providing smart order routing, liquidation protection fences, and fill-quality analytics on top of major CEX APIs including Binance, Bybit, and OKX.',
  },

  // ── AI Bybit Trade ──
  {
    id: 'ai-bybit-trade',
    name: 'ai-bybit-trade',
    author: 'AI Trading Labs',
    authorHandle: '@aitradinglabs',
    tag: 'Skill',
    category: 'trading',
    description: 'AI-powered Bybit trading skill. LLM-driven signal generation, automated position sizing, and execution management on Bybit perps and spot.',
    downloads: 18600,
    stars: 930,
    version: '1.0.0',
    installCommand: '> install the ai-bybit-trade skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/ai-bybit-trade',
    usageExample: '> generate AI signal\n> auto-size position\n> execute on bybit',
    detailedDescription: 'AI-powered Bybit trading integration combining LLM-based market signal generation with automated position sizing and risk management for Bybit spot and perpetual futures markets.',
  },

  // ── AI Bybit Trader ──
  {
    id: 'ai-bybit-trader',
    name: 'ai-bybit-trader',
    author: 'AI Trading Labs',
    authorHandle: '@aitradinglabs',
    tag: 'Skill',
    category: 'trading',
    description: 'Full AI trader agent for Bybit. End-to-end autonomous trading including market analysis, strategy selection, trade execution, and position monitoring.',
    downloads: 15800,
    stars: 790,
    version: '1.1.0',
    installCommand: '> install the ai-bybit-trader skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/ai-bybit-trader',
    usageExample: '> start ai trader\n> analyze market\n> monitor positions',
    detailedDescription: 'Full autonomous AI trader for Bybit combining market regime detection, multi-timeframe analysis, strategy selection, automated execution, and continuous position monitoring with adaptive stop-loss.',
    trending: true,
  },

  // ── Apify Crypto Skill ──
  {
    id: 'apify-crypto-skill',
    name: 'apify-crypto-skill',
    author: 'Apify',
    authorHandle: '@apify',
    tag: 'Skill',
    category: 'analytics',
    description: 'Apify crypto data scraping skill. Scrape exchange prices, social sentiment, news feeds, and whale wallet data from public web sources via Apify actors.',
    downloads: 9800,
    stars: 490,
    version: '1.0.0',
    installCommand: '> install the apify-crypto-skill skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/apify-crypto-skill',
    usageExample: '> scrape exchange data\n> collect social sentiment\n> track whale wallets',
    detailedDescription: 'Apify web scraping platform integration for crypto market data collection. Run actors for exchange price scraping, Twitter/Reddit sentiment, news aggregation, and on-chain whale wallet monitoring.',
  },

  // ── Binance Arbitrage Finder ──
  {
    id: 'binance-arbitrage-finder',
    name: 'binance-arbitrage-finder',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance cross-market arbitrage finder. Detect triangular, statistical, and cross-exchange arbitrage opportunities on Binance in real time.',
    downloads: 24600,
    stars: 1230,
    version: '1.0.0',
    installCommand: '> install the binance-arbitrage-finder skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-arbitrage-finder',
    usageExample: '> find triangular arb\n> detect stat arb\n> execute arbitrage',
    detailedDescription: 'Binance arbitrage detection engine scanning for triangular, statistical, and cross-exchange price discrepancies in real time with latency-aware execution readiness scoring.',
  },

  // ── Binance Futures Trading ──
  {
    id: 'binance-futures-trading',
    name: 'binance-futures-trading',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance Futures all-in-one trading skill. USDS-M and COIN-M futures with TP/SL, trailing stops, leverage management, and funding rate alerts.',
    downloads: 38400,
    stars: 1920,
    version: '2.0.0',
    installCommand: '> install the binance-futures-trading skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-futures-trading',
    usageExample: '> open futures long\n> set trailing stop\n> check funding rate',
    detailedDescription: 'Binance futures comprehensive trading skill covering USDS-M and COIN-M perpetuals with advanced order types (TP/SL, trailing stop, post-only), leverage adjustment, and real-time funding rate monitoring.',
    featured: true,
  },

  // ── Binance HeyAnon ──
  {
    id: 'binance-heyanon',
    name: 'binance-heyanon',
    author: 'HeyAnon',
    authorHandle: '@heyanon_ai',
    tag: 'Skill',
    category: 'trading',
    description: 'HeyAnon Binance integration. Natural language interface for Binance trading — place orders, check balances, and get market data via conversational AI.',
    downloads: 14200,
    stars: 710,
    version: '1.0.0',
    installCommand: '> install the binance-heyanon skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-heyanon',
    usageExample: '> buy 0.1 BTC on binance\n> show my balance\n> what is ETH price',
    detailedDescription: 'HeyAnon conversational AI interface for Binance. Execute trades, query balances, monitor orders, and access market data through natural language commands with intent parsing and safety confirmation.',
  },

  // ── Binance Official Algo ──
  {
    id: 'binance-official-algo',
    name: 'binance-official-algo',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance Official Algo trading skill. TWAP, VWAP, Iceberg, and other algorithmic order types for institutional-grade execution on Binance.',
    downloads: 22800,
    stars: 1140,
    version: '1.0.0',
    installCommand: '> install the binance-official-algo skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-algo',
    usageExample: '> execute TWAP order\n> place iceberg\n> run VWAP strategy',
    detailedDescription: 'Official Binance algorithmic execution API supporting TWAP, VWAP, Iceberg, and bracket orders for institutional-grade trade execution with minimized market impact and slippage.',
  },

  // ── Binance Official Alpha ──
  {
    id: 'binance-official-alpha',
    name: 'binance-official-alpha',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'analytics',
    description: 'Binance Alpha early-access signal skill. Access Binance\'s internal alpha signals for emerging tokens, DeFi opportunities, and early trend detection.',
    downloads: 18600,
    stars: 930,
    version: '1.0.0',
    installCommand: '> install the binance-official-alpha skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-alpha',
    usageExample: '> get alpha signals\n> find emerging tokens\n> track defi trends',
    detailedDescription: 'Binance Alpha program integration providing early access to emerging token signals, DeFi opportunity alerts, and trend detection data from Binance\'s research and on-chain analytics team.',
    trending: true,
  },

  // ── Binance Official Assets ──
  {
    id: 'binance-official-assets',
    name: 'binance-official-assets',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'tools',
    description: 'Binance asset management skill. Query multi-wallet balances, transfer between sub-accounts, manage dust, and track portfolio PnL across Binance accounts.',
    downloads: 16400,
    stars: 820,
    version: '1.0.0',
    installCommand: '> install the binance-official-assets skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-assets',
    usageExample: '> check all balances\n> transfer to sub-account\n> convert dust to BNB',
    detailedDescription: 'Binance asset management skill for querying balances across spot/futures/earn wallets, inter-account transfers, dust conversion to BNB, and cross-account portfolio PnL tracking.',
  },

  // ── Binance Official Convert ──
  {
    id: 'binance-official-convert',
    name: 'binance-official-convert',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance Convert skill. Instant fee-free token conversion on Binance with real-time quotes, bulk conversion, and auto-conversion settings.',
    downloads: 21600,
    stars: 1080,
    version: '1.0.0',
    installCommand: '> install the binance-official-convert skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-convert',
    usageExample: '> convert USDT to BTC\n> bulk convert tokens\n> set auto-convert',
    detailedDescription: 'Binance Convert API integration for instant zero-fee token swaps, bulk conversion of multiple assets to USDC/USDT, and automated conversion rules for incoming deposits.',
  },

  // ── Binance Official Derivatives – COIN Futures ──
  {
    id: 'binance-official-derivatives-coin-futures',
    name: 'binance-official-derivatives-trading-coin-futures',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance COIN-M Futures skill. Trade BTC/ETH/BNB coin-margined perpetuals with cross/isolated margin, delivery futures, and portfolio management.',
    downloads: 28400,
    stars: 1420,
    version: '1.0.0',
    installCommand: '> install the binance-official-derivatives-trading-coin-futures skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-derivatives-trading-coin-futures',
    usageExample: '> trade BTC coin-m futures\n> set cross margin\n> query delivery price',
    detailedDescription: 'Binance COIN-M futures integration for coin-margined perpetuals and quarterly delivery contracts. Cross/isolated margin modes, basis trading, and real-time mark/delivery price tracking.',
  },

  // ── Binance Official Derivatives – Options ──
  {
    id: 'binance-official-derivatives-options',
    name: 'binance-official-derivatives-trading-options',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance European Options skill. Trade BTC/ETH European-style options, view options chain, calculate greeks, and hedge spot exposure with puts/calls.',
    downloads: 19200,
    stars: 960,
    version: '1.0.0',
    installCommand: '> install the binance-official-derivatives-trading-options skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-derivatives-trading-options',
    usageExample: '> buy BTC call option\n> view options chain\n> calculate delta',
    detailedDescription: 'Binance European options integration for BTC/ETH call/put trading. Access full options chain, real-time Greeks calculation, implied volatility surface, and portfolio hedge suggestions.',
  },

  // ── Binance Official Derivatives – Portfolio Margin ──
  {
    id: 'binance-official-derivatives-portfolio-margin',
    name: 'binance-official-derivatives-trading-portfolio-margin',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance Portfolio Margin skill. Manage unified cross-margin accounts combining spot, futures, and options with reduced margin requirements.',
    downloads: 16800,
    stars: 840,
    version: '1.0.0',
    installCommand: '> install the binance-official-derivatives-trading-portfolio-margin skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-derivatives-trading-portfolio-margin',
    usageExample: '> open PM account\n> view margin ratio\n> hedge with options',
    detailedDescription: 'Binance Portfolio Margin account management providing cross-margined spot, futures, and options positions with offset benefits, unified margin ratios, and enhanced capital efficiency.',
  },

  // ── Binance Official Derivatives – Portfolio Margin Pro ──
  {
    id: 'binance-official-derivatives-portfolio-margin-pro',
    name: 'binance-official-derivatives-trading-portfolio-margin-pro',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance Portfolio Margin Pro skill. Advanced PM Pro with enhanced cross-margining, more asset offsets, and institutional credit limits.',
    downloads: 12400,
    stars: 620,
    version: '1.0.0',
    installCommand: '> install the binance-official-derivatives-trading-portfolio-margin-pro skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-derivatives-trading-portfolio-margin-pro',
    usageExample: '> upgrade to PM Pro\n> view enhanced offsets\n> set credit limits',
    detailedDescription: 'Binance Portfolio Margin Pro for institutional traders with extended asset offset support, higher credit limits, enhanced cross-margining across all product types, and dedicated risk dashboard.',
  },

  // ── Binance Official Margin Trading ──
  {
    id: 'binance-official-margin-trading',
    name: 'binance-official-margin-trading',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance Margin Trading skill. Borrow assets, trade on cross/isolated margin, manage interest, and monitor liquidation thresholds on Binance.',
    downloads: 32400,
    stars: 1620,
    version: '1.0.0',
    installCommand: '> install the binance-official-margin-trading skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-margin-trading',
    usageExample: '> borrow USDT\n> trade on margin\n> check margin level',
    detailedDescription: 'Binance margin trading integration for cross and isolated margin accounts. Borrow assets at competitive rates, execute leveraged spot trades, monitor liquidation prices, and manage interest repayment.',
  },

  // ── Binance Official Onchain Pay ──
  {
    id: 'binance-official-onchain-pay',
    name: 'binance-official-onchain-pay',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'tools',
    description: 'Binance On-Chain Pay skill. Send and receive crypto payments on-chain through Binance\'s payment infrastructure with merchant integration support.',
    downloads: 14200,
    stars: 710,
    version: '1.0.0',
    installCommand: '> install the binance-official-onchain-pay skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-onchain-pay',
    usageExample: '> send payment\n> create payment link\n> track payment status',
    detailedDescription: 'Binance On-Chain Pay integration for crypto payment processing. Create payment orders, generate QR codes, track transaction status, and integrate merchant callbacks for e-commerce workflows.',
  },

  // ── Binance Official Square Post ──
  {
    id: 'binance-official-square-post',
    name: 'binance-official-square-post',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'social',
    description: 'Binance Square content publishing skill. Post trading insights, market analysis, and AI-generated content to Binance Square for community reach.',
    downloads: 8600,
    stars: 430,
    version: '1.0.0',
    installCommand: '> install the binance-official-square-post skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-square-post',
    usageExample: '> post market analysis\n> share trade insight\n> publish ai content',
    detailedDescription: 'Binance Square publishing API for posting trading insights, market analysis, and AI-generated content to the Binance social trading community with engagement analytics.',
  },

  // ── Binance Official Sub-Account ──
  {
    id: 'binance-official-sub-account',
    name: 'binance-official-sub-account',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'tools',
    description: 'Binance sub-account management skill. Create, manage, and monitor multiple Binance sub-accounts for strategy segregation, risk isolation, and fund management.',
    downloads: 16200,
    stars: 810,
    version: '1.0.0',
    installCommand: '> install the binance-official-sub-account skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-sub-account',
    usageExample: '> create sub-account\n> transfer funds\n> monitor sub P&L',
    detailedDescription: 'Binance sub-account management for creating strategy-specific sub-accounts, inter-account fund transfers, consolidated portfolio views, API key management, and cross-account trade monitoring.',
  },

  // ── Binance Official Web3 – Crypto Market Rank ──
  {
    id: 'binance-official-web3-crypto-market-rank',
    name: 'binance-official-web3-crypto-market-rank',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'analytics',
    description: 'Binance Web3 crypto market ranking. Token discovery, trending rankings, market cap data, and on-chain metrics from Binance\'s Web3 market intelligence.',
    downloads: 22400,
    stars: 1120,
    version: '1.0.0',
    installCommand: '> install the binance-official-web3-crypto-market-rank skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-web3-crypto-market-rank',
    usageExample: '> get trending tokens\n> check market rank\n> find new listings',
    detailedDescription: 'Binance Web3 market ranking data covering token trending scores, market cap rankings, on-chain activity metrics, new listing alerts, and price momentum signals from Binance\'s Web3 data platform.',
  },

  // ── Binance Official Web3 – Meme Rush ──
  {
    id: 'binance-official-web3-meme-rush',
    name: 'binance-official-web3-meme-rush',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'analytics',
    description: 'Binance Meme Rush token tracker. Track meme token launches, social velocity, liquidity events, and early whale activity on Binance Web3.',
    downloads: 18800,
    stars: 940,
    version: '1.0.0',
    installCommand: '> install the binance-official-web3-meme-rush skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-web3-meme-rush',
    usageExample: '> track new meme tokens\n> check social velocity\n> find early entries',
    detailedDescription: 'Binance Meme Rush platform integration for tracking meme token launches, social media velocity, liquidity pool events, and early whale accumulation signals for meme coin opportunities.',
    trending: true,
  },

  // ── Binance Official Web3 – Query Address Info ──
  {
    id: 'binance-official-web3-query-address-info',
    name: 'binance-official-web3-query-address-info',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'analytics',
    description: 'Binance Web3 address intelligence. Query wallet profiles, transaction history, DeFi positions, and risk scores for any address via Binance Web3 API.',
    downloads: 16400,
    stars: 820,
    version: '1.0.0',
    installCommand: '> install the binance-official-web3-query-address-info skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-web3-query-address-info',
    usageExample: '> query wallet profile\n> check address risk\n> view defi positions',
    detailedDescription: 'Binance Web3 address intelligence API for querying wallet profiles, transaction histories, DeFi protocol positions, NFT holdings, and risk scores for any EVM address.',
  },

  // ── Binance Official Web3 – Query Token Audit ──
  {
    id: 'binance-official-web3-query-token-audit',
    name: 'binance-official-web3-query-token-audit',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'analytics',
    description: 'Binance Web3 token security audit. Analyze smart contract risk, honeypot detection, liquidity lock status, and rug pull indicators from Binance.',
    downloads: 24600,
    stars: 1230,
    version: '1.0.0',
    installCommand: '> install the binance-official-web3-query-token-audit skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-web3-query-token-audit',
    usageExample: '> audit token contract\n> check honeypot\n> verify liquidity lock',
    detailedDescription: 'Binance Web3 token security audit service analyzing smart contract vulnerabilities, honeypot patterns, ownership renouncement, liquidity lock verification, and rug pull risk scoring.',
  },

  // ── Binance Official Web3 – Query Token Info ──
  {
    id: 'binance-official-web3-query-token-info',
    name: 'binance-official-web3-query-token-info',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'analytics',
    description: 'Binance Web3 token metadata. Fetch comprehensive token data including price, market cap, holders, volume, and on-chain metrics from Binance Web3.',
    downloads: 19800,
    stars: 990,
    version: '1.0.0',
    installCommand: '> install the binance-official-web3-query-token-info skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-web3-query-token-info',
    usageExample: '> get token info\n> check holder count\n> view on-chain volume',
    detailedDescription: 'Binance Web3 token information API providing price history, market cap, circulating supply, holder distribution, on-chain trading volume, and social metrics for any token contract.',
  },

  // ── Binance Official Web3 – Trading Signal ──
  {
    id: 'binance-official-web3-trading-signal',
    name: 'binance-official-web3-trading-signal',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'analytics',
    description: 'Binance Web3 AI trading signals. Access Binance\'s AI-generated buy/sell signals for crypto tokens based on on-chain, technical, and sentiment analysis.',
    downloads: 32600,
    stars: 1630,
    version: '1.0.0',
    installCommand: '> install the binance-official-web3-trading-signal skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-official-web3-trading-signal',
    usageExample: '> get AI signals\n> filter by confidence\n> alert on signal',
    detailedDescription: 'Binance Web3 AI trading signal service providing buy/sell signals generated from on-chain analysis, technical indicators, and market sentiment with confidence scores and signal history.',
    trending: true,
  },

  // ── Binance Price Alerts ──
  {
    id: 'binance-price-alerts',
    name: 'binance-price-alerts',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'analytics',
    description: 'Binance price alert automation. Set and manage customizable price, volume, and indicator-based alerts for any Binance-listed asset.',
    downloads: 34600,
    stars: 1730,
    version: '1.0.0',
    installCommand: '> install the binance-price-alerts skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-price-alerts',
    usageExample: '> set price alert\n> volume spike alert\n> RSI alert',
    detailedDescription: 'Binance alert management system for setting price thresholds, volume anomaly notifications, technical indicator alerts (RSI, MACD, Bollinger Bands), and custom webhook callbacks.',
  },

  // ── Binance Signal Engine ──
  {
    id: 'binance-signal-engine',
    name: 'binance-signal-engine',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'analytics',
    description: 'Binance trading signal engine. Multi-strategy signal generation combining technical, on-chain, and sentiment data with backtested performance metrics.',
    downloads: 28800,
    stars: 1440,
    version: '1.0.0',
    installCommand: '> install the binance-signal-engine skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-signal-engine',
    usageExample: '> generate signals\n> backtest strategy\n> subscribe to signal feed',
    detailedDescription: 'Binance signal engine combining technical analysis, on-chain flow data, and market sentiment into actionable trading signals with backtested Sharpe ratios and drawdown metrics.',
  },

  // ── Binance Skills Hub ──
  {
    id: 'binance-skills-hub',
    name: 'binance-skills-hub',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'tools',
    description: 'Binance Skills Hub. Central registry and launcher for all official Binance AI agent skills — discover, install, and manage the full Binance skill suite.',
    downloads: 42600,
    stars: 2130,
    version: '1.0.0',
    installCommand: '> install the binance-skills-hub skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-skills-hub',
    usageExample: '> browse binance skills\n> install skill bundle\n> update all skills',
    detailedDescription: 'Binance Skills Hub central registry providing discovery, installation, and lifecycle management for all official Binance AI agent skills including trading, analytics, Web3, and utility modules.',
    featured: true,
  },

  // ── Binance Spot API ──
  {
    id: 'binance-spot-api',
    name: 'binance-spot-api',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance Spot REST/WebSocket API. Direct low-level API access for spot trading, order management, depth streaming, and kline data.',
    downloads: 64800,
    stars: 3240,
    version: '3.0.0',
    installCommand: '> install the binance-spot-api skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-spot-api',
    usageExample: '> place spot order\n> stream order book\n> get klines',
    detailedDescription: 'Binance Spot API v3 direct integration with REST and WebSocket support for order placement, order book streaming, kline data, account info, and signed trading requests.',
  },

  // ── Binance Spot Skill ──
  {
    id: 'binance-spot-skill',
    name: 'binance-spot-skill',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance Spot higher-level skill. Pre-built spot trading strategies including DCA, momentum, mean-reversion, and breakout on Binance spot markets.',
    downloads: 38400,
    stars: 1920,
    version: '1.0.0',
    installCommand: '> install the binance-spot-skill skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-spot-skill',
    usageExample: '> run DCA strategy\n> trade momentum\n> breakout alert',
    detailedDescription: 'Binance Spot skill with pre-built strategy templates for DCA, momentum chasing, mean-reversion, and price breakout trading. Includes position sizing, entry/exit logic, and PnL tracking.',
  },

  // ── Binance Spot Trader ──
  {
    id: 'binance-spot-trader',
    name: 'binance-spot-trader',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance automated spot trader. Fully automated spot trading agent with signal intake, smart order routing, risk management, and execution logging.',
    downloads: 29600,
    stars: 1480,
    version: '1.0.0',
    installCommand: '> install the binance-spot-trader skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-spot-trader',
    usageExample: '> start auto trader\n> inject signal\n> view trade log',
    detailedDescription: 'Binance fully automated spot trader agent accepting external signals, routing orders intelligently, enforcing position limits and drawdown stops, and logging all executions with performance analytics.',
  },

  // ── Binance Spot Trading ──
  {
    id: 'binance-spot-trading',
    name: 'binance-spot-trading',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance spot trading essentials. One-click market/limit orders, OCO orders, order tracking, and real-time portfolio snapshot for Binance spot.',
    downloads: 48600,
    stars: 2430,
    version: '2.0.0',
    installCommand: '> install the binance-spot-trading skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-spot-trading',
    usageExample: '> buy BTC market\n> place OCO order\n> track open orders',
    detailedDescription: 'Binance spot trading skill providing streamlined order placement (market, limit, OCO), real-time order tracking, portfolio balance snapshots, and trade history exports.',
  },

  // ── Binance Spot WebSocket ──
  {
    id: 'binance-spot-websocket',
    name: 'binance-spot-websocket',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'tools',
    description: 'Binance Spot WebSocket streams. Subscribe to real-time ticker, depth, trade, kline, and user data streams via Binance WebSocket for low-latency data.',
    downloads: 32400,
    stars: 1620,
    version: '1.0.0',
    installCommand: '> install the binance-spot-websocket skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-spot-websocket',
    usageExample: '> subscribe ticker\n> stream order book\n> user data stream',
    detailedDescription: 'Binance WebSocket stream manager for subscribing to real-time spot market data (ticker, depth, trades, klines) and user account streams with automatic reconnection and message parsing.',
  },

  // ── Binance Square Post ──
  {
    id: 'binance-square-post',
    name: 'binance-square-post',
    author: 'Binance Community',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'social',
    description: 'Binance Square community posts. Create, schedule, and publish trading-related content on Binance Square with AI assistance and engagement tracking.',
    downloads: 9600,
    stars: 480,
    version: '1.0.0',
    installCommand: '> install the binance-square-post skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-square-post',
    usageExample: '> draft square post\n> schedule publication\n> check engagement',
    detailedDescription: 'Binance Square community publishing skill for creating, scheduling, and publishing trading insights to Binance Square. AI-assisted content drafting with engagement analytics and follower metrics.',
  },

  // ── Binance Stat Arb Monitor ──
  {
    id: 'binance-stat-arb-monitor',
    name: 'binance-stat-arb-monitor',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'analytics',
    description: 'Binance statistical arbitrage monitor. Track cointegrated pairs, z-score divergences, and mean-reversion setups across Binance spot and futures markets.',
    downloads: 14800,
    stars: 740,
    version: '1.0.0',
    installCommand: '> install the binance-stat-arb-monitor skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-stat-arb-monitor',
    usageExample: '> find cointegrated pairs\n> check z-score\n> alert on divergence',
    detailedDescription: 'Binance statistical arbitrage monitoring system identifying cointegrated asset pairs, tracking z-score divergence, and alerting when mean-reversion setups emerge across spot and futures markets.',
  },

  // ── Binance Trading Assistant ──
  {
    id: 'binance-trading-assistant',
    name: 'binance-trading-assistant',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'trading',
    description: 'Binance AI trading assistant. Conversational trading helper providing market context, order assistance, risk alerts, and strategy recommendations on Binance.',
    downloads: 38600,
    stars: 1930,
    version: '1.0.0',
    installCommand: '> install the binance-trading-assistant skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-trading-assistant',
    usageExample: '> ask about BTC trend\n> help me place order\n> explain my positions',
    detailedDescription: 'Binance conversational trading assistant providing natural language market context, order placement guidance, real-time risk alerts, and personalized strategy recommendations based on portfolio state.',
    featured: true,
  },

  // ── Binance Watchlist Scanner ──
  {
    id: 'binance-watchlist-scanner',
    name: 'binance-watchlist-scanner',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'analytics',
    description: 'Binance watchlist scanner. Scan your Binance watchlist for technical pattern triggers, volume anomalies, and price alerts with configurable thresholds.',
    downloads: 22400,
    stars: 1120,
    version: '1.0.0',
    installCommand: '> install the binance-watchlist-scanner skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-watchlist-scanner',
    usageExample: '> scan watchlist\n> alert on breakout\n> volume anomaly scan',
    detailedDescription: 'Binance watchlist monitoring tool scanning user-defined asset lists for technical pattern triggers (breakouts, reversals), volume anomalies, and custom price thresholds with configurable notification channels.',
  },

  // ── Binance Web3 API ──
  {
    id: 'binance-web3-api',
    name: 'binance-web3-api',
    author: 'Binance',
    authorHandle: '@binance',
    tag: 'Skill',
    category: 'tools',
    description: 'Binance Web3 API gateway. Multi-chain RPC access, on-chain data queries, NFT APIs, and DApp connectivity via Binance\'s Web3 infrastructure.',
    downloads: 28600,
    stars: 1430,
    version: '1.0.0',
    installCommand: '> install the binance-web3-api skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/binance-web3-api',
    usageExample: '> query on-chain data\n> call smart contract\n> fetch NFT metadata',
    detailedDescription: 'Binance Web3 API providing multi-chain RPC endpoints, on-chain token analytics, NFT metadata, smart contract interaction, and DApp connectivity across Ethereum, BNB Chain, and other supported networks.',
  },

  // ── Bitfinex Lending Monitor ──
  {
    id: 'bitfinex-lending-monitor',
    name: 'bitfinex-lending-monitor',
    author: 'Bitfinex',
    authorHandle: '@bitfinex',
    tag: 'Skill',
    category: 'defi',
    description: 'Bitfinex P2P lending monitor. Track funding rates, auto-manage lending offers, optimize APY, and monitor active loans on Bitfinex\'s margin funding market.',
    downloads: 12800,
    stars: 640,
    version: '1.0.0',
    installCommand: '> install the bitfinex-lending-monitor skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/bitfinex-lending-monitor',
    usageExample: '> monitor lending rates\n> optimize offers\n> track active loans',
    detailedDescription: 'Bitfinex P2P margin funding market monitor for tracking live USD/crypto lending rates, automated offer placement at optimal rates, APY optimization, and active loan portfolio management.',
  },

  // ── Bitget API ──
  {
    id: 'bitget-api',
    name: 'bitget-api',
    author: 'Bitget',
    authorHandle: '@bitgetglobal',
    tag: 'Skill',
    category: 'trading',
    description: 'Bitget V2 REST/WebSocket API. Direct API access for spot, futures, copy-trade, and options on Bitget with comprehensive endpoint coverage.',
    downloads: 28600,
    stars: 1430,
    version: '2.0.0',
    installCommand: '> install the bitget-api skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/bitget-api',
    usageExample: '> place bitget order\n> stream futures data\n> query copy traders',
    detailedDescription: 'Bitget V2 API integration covering spot, USDT/COIN-M perpetuals, options, copy-trading, and earn products with full REST and WebSocket support for real-time market and account data.',
  },

  // ── Bitget Official Macro Analyst ──
  {
    id: 'bitget-official-macro-analyst',
    name: 'bitget-official-macro-analyst',
    author: 'Bitget',
    authorHandle: '@bitgetglobal',
    tag: 'Skill',
    category: 'analytics',
    description: 'Bitget macro analysis skill. AI-generated macro market commentary, Fed policy impact analysis, and crypto cycle positioning from Bitget Research.',
    downloads: 16400,
    stars: 820,
    version: '1.0.0',
    installCommand: '> install the bitget-official-macro-analyst skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/bitget-official-macro-analyst',
    usageExample: '> get macro analysis\n> fed policy impact\n> cycle positioning',
    detailedDescription: 'Bitget Research macro analysis service providing AI-generated commentary on global macro trends, Federal Reserve policy impacts on crypto markets, and Bitcoin cycle positioning insights.',
  },

  // ── Bitget Official Market Intel ──
  {
    id: 'bitget-official-market-intel',
    name: 'bitget-official-market-intel',
    author: 'Bitget',
    authorHandle: '@bitgetglobal',
    tag: 'Skill',
    category: 'analytics',
    description: 'Bitget market intelligence. Token-level analytics including order flow, large trade detection, market maker activity, and liquidity depth analysis.',
    downloads: 19800,
    stars: 990,
    version: '1.0.0',
    installCommand: '> install the bitget-official-market-intel skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/bitget-official-market-intel',
    usageExample: '> analyze order flow\n> detect large trades\n> view MM activity',
    detailedDescription: 'Bitget market intelligence platform providing order flow analysis, large trade detection, market maker activity monitoring, and real-time liquidity depth analytics for informed trading decisions.',
  },

  // ── Bitget Official News Briefing ──
  {
    id: 'bitget-official-news-briefing',
    name: 'bitget-official-news-briefing',
    author: 'Bitget',
    authorHandle: '@bitgetglobal',
    tag: 'Skill',
    category: 'analytics',
    description: 'Bitget AI news briefing. Curated and summarized crypto news with market impact scoring, narrative tracking, and AI-powered sentiment analysis.',
    downloads: 22400,
    stars: 1120,
    version: '1.0.0',
    installCommand: '> install the bitget-official-news-briefing skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/bitget-official-news-briefing',
    usageExample: '> get news briefing\n> check impact score\n> track narratives',
    detailedDescription: 'Bitget AI-powered news aggregation and summarization service with market impact scoring, narrative trend tracking, and real-time sentiment analysis across 500+ crypto news sources.',
  },

  // ── Bitget Official Sentiment Analyst ──
  {
    id: 'bitget-official-sentiment-analyst',
    name: 'bitget-official-sentiment-analyst',
    author: 'Bitget',
    authorHandle: '@bitgetglobal',
    tag: 'Skill',
    category: 'analytics',
    description: 'Bitget sentiment analysis. Real-time social sentiment scores, fear & greed index, Twitter/Telegram sentiment, and contrarian signal generation.',
    downloads: 18600,
    stars: 930,
    version: '1.0.0',
    installCommand: '> install the bitget-official-sentiment-analyst skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/bitget-official-sentiment-analyst',
    usageExample: '> get sentiment score\n> check fear greed\n> contrarian signal',
    detailedDescription: 'Bitget sentiment analytics aggregating Twitter, Telegram, Reddit, and on-chain behavior into real-time sentiment scores, fear & greed indices, and contrarian trading signals.',
  },

  // ── Bitget Official Technical Analysis ──
  {
    id: 'bitget-official-technical-analysis',
    name: 'bitget-official-technical-analysis',
    author: 'Bitget',
    authorHandle: '@bitgetglobal',
    tag: 'Skill',
    category: 'analytics',
    description: 'Bitget technical analysis engine. Automated chart pattern recognition, indicator signals (RSI/MACD/BB), S/R levels, and trade setup scoring.',
    downloads: 28800,
    stars: 1440,
    version: '1.0.0',
    installCommand: '> install the bitget-official-technical-analysis skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/bitget-official-technical-analysis',
    usageExample: '> scan for patterns\n> get RSI signals\n> find support levels',
    detailedDescription: 'Bitget automated technical analysis covering chart pattern recognition (H&S, double tops, flags), RSI/MACD/Bollinger Band signals, dynamic support/resistance levels, and trade setup confidence scores.',
  },

  // ── Bitget Wallet ──
  {
    id: 'bitget-wallet',
    name: 'bitget-wallet',
    author: 'Bitget',
    authorHandle: '@bitgetglobal',
    tag: 'Skill',
    category: 'tools',
    description: 'Bitget Wallet multichain skill. Self-custody wallet with DEX swaps, NFT management, cross-chain bridging, and DApp browser across 100+ networks.',
    downloads: 28400,
    stars: 1420,
    version: '3.0.0',
    installCommand: '> install the bitget-wallet skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/bitget-wallet',
    usageExample: '> swap on bitget wallet\n> bridge to arbitrum\n> manage NFTs',
    detailedDescription: 'Bitget Wallet self-custody integration for multichain asset management, aggregated DEX swaps, cross-chain bridging, NFT portfolio management, and seamless DApp connectivity across 100+ EVM and non-EVM networks.',
  },

  // ── Bybit AI Skills ──
  {
    id: 'bybit-ai-skills',
    name: 'bybit-ai-skills',
    author: 'Bybit',
    authorHandle: '@bybit_official',
    tag: 'Skill',
    category: 'trading',
    description: 'Bybit official AI skills bundle. AI-assisted trading signals, LLM market analysis, automated strategy suggestions, and intelligent risk management for Bybit.',
    downloads: 42600,
    stars: 2130,
    version: '1.0.0',
    installCommand: '> install the bybit-ai-skills skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/bybit-ai-skills',
    usageExample: '> get AI signal\n> auto-suggest strategy\n> AI risk check',
    detailedDescription: 'Official Bybit AI skills bundle integrating LLM-powered market analysis, AI trading signal generation, automated strategy suggestions, and intelligent risk management recommendations for Bybit traders.',
    featured: true,
    trending: true,
  },

  // ── Bybit Futures ──
  {
    id: 'bybit-futures',
    name: 'bybit-futures',
    author: 'Bybit',
    authorHandle: '@bybit_official',
    tag: 'Skill',
    category: 'trading',
    description: 'Bybit futures specialist. Advanced perp management including scaled orders, TWAP, isolated/cross margin toggling, and funding rate monitoring.',
    downloads: 36400,
    stars: 1820,
    version: '1.0.0',
    installCommand: '> install the bybit-futures skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/bybit-futures',
    usageExample: '> open bybit perp\n> execute scaled entry\n> monitor funding',
    detailedDescription: 'Bybit futures advanced skill covering USDT/USDC perpetuals, scaled order execution, TWAP fills, isolated/cross margin management, funding rate monitoring, and liquidation price tracking.',
  },

  // ── Bybit Trading ──
  {
    id: 'bybit-trading',
    name: 'bybit-trading',
    author: 'Bybit',
    authorHandle: '@bybit_official',
    tag: 'Skill',
    category: 'trading',
    description: 'Bybit unified trading skill. One-stop access to Bybit spot, perps, options, and copy-trading with consolidated position management.',
    downloads: 48600,
    stars: 2430,
    version: '2.0.0',
    installCommand: '> install the bybit-trading skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/bybit-trading',
    usageExample: '> trade bybit spot\n> copy top trader\n> manage all positions',
    detailedDescription: 'Bybit unified trading interface covering the full Unified Trading Account — spot, linear/inverse perpetuals, options, and copy-trading with consolidated balance and position management.',
  },

  // ── Cerebrus Pulse MCP ──
  {
    id: 'cerebrus-pulse-mcp',
    name: 'cerebrus-pulse-mcp',
    author: 'Cerebrus',
    authorHandle: '@cerebrus_io',
    tag: 'Skill',
    category: 'analytics',
    description: 'Cerebrus Pulse MCP server. Real-time cross-exchange market pulse including order flow, funding rate divergence, and liquidation cluster analytics.',
    downloads: 11400,
    stars: 570,
    version: '1.0.0',
    installCommand: '> install the cerebrus-pulse-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/cerebrus-pulse-mcp',
    usageExample: '> check market pulse\n> compare funding rates\n> find liquidation clusters',
    detailedDescription: 'Cerebrus Pulse MCP server providing cross-exchange market intelligence including order flow comparison, funding rate divergence between Binance/Bybit/OKX, and liquidation cluster heatmaps.',
  },

  // ── Coinbase X404 ──
  {
    id: 'coinbase-x404',
    name: 'coinbase-x404',
    author: 'Coinbase',
    authorHandle: '@coinbase',
    tag: 'Skill',
    category: 'tools',
    description: 'Coinbase X404 experimental features. Access Coinbase beta APIs, experimental trading features, and early-access protocol integrations.',
    downloads: 8600,
    stars: 430,
    version: '0.4.0',
    installCommand: '> install the coinbase-x404 skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/coinbase-x404',
    usageExample: '> access beta features\n> try experimental API\n> early protocol access',
    detailedDescription: 'Coinbase X404 experimental feature access providing early integration with beta Coinbase APIs, experimental trading capabilities, and pre-launch protocol integrations for developers.',
  },

  // ── Coinbase ──
  {
    id: 'coinbase-exchange',
    name: 'coinbase',
    author: 'Coinbase',
    authorHandle: '@coinbase',
    tag: 'Skill',
    category: 'trading',
    description: 'Coinbase core exchange skill. Spot trading, portfolio management, staking, and crypto purchases via the main Coinbase platform API.',
    downloads: 52400,
    stars: 2620,
    version: '1.0.0',
    installCommand: '> install the coinbase skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/coinbase',
    usageExample: '> buy crypto on coinbase\n> view portfolio\n> stake ETH',
    detailedDescription: 'Coinbase core platform integration covering spot trading, portfolio tracking, crypto purchases via bank/card, ETH/SOL staking, and account management through the Coinbase API.',
    featured: true,
  },

  // ── Crypto CEX ──
  {
    id: 'crypto-cex',
    name: 'crypto-cex',
    author: 'CryptoCEX',
    authorHandle: '@cryptocex_io',
    tag: 'Skill',
    category: 'trading',
    description: 'Multi-CEX unified trading skill. Route and execute orders across Binance, Bybit, OKX, and Gate.io from a single unified interface.',
    downloads: 24600,
    stars: 1230,
    version: '1.0.0',
    installCommand: '> install the crypto-cex skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/crypto-cex',
    usageExample: '> compare CEX prices\n> route best order\n> unified portfolio view',
    detailedDescription: 'Multi-CEX trading aggregator connecting Binance, Bybit, OKX, and Gate.io for unified order routing, best-price execution, consolidated portfolio views, and cross-exchange arbitrage.',
  },

  // ── Crypto.com MCP ──
  {
    id: 'crypto-com-mcp',
    name: 'crypto-com-mcp',
    author: 'Crypto.com',
    authorHandle: '@cryptocom',
    tag: 'Skill',
    category: 'trading',
    description: 'Crypto.com official MCP server. Access Crypto.com Exchange, DeFi Wallet, and Visa Card features via Model Context Protocol.',
    downloads: 18400,
    stars: 920,
    version: '1.0.0',
    installCommand: '> install the crypto-com-mcp skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/crypto-com-mcp',
    usageExample: '> trade on crypto.com\n> check card rewards\n> query wallet',
    detailedDescription: 'Official Crypto.com MCP server providing access to the Crypto.com Exchange, DeFi Wallet operations, Visa Card reward tracking, CRO staking, and Cronos chain interactions via MCP interfaces.',
  },

  // ── Crypto.com Official Agent Trading ──
  {
    id: 'cryptocom-official-agent-trading',
    name: 'cryptocom-official-agent-trading',
    author: 'Crypto.com',
    authorHandle: '@cryptocom',
    tag: 'Skill',
    category: 'trading',
    description: 'Official Crypto.com agent trading. Autonomous trading on Crypto.com Exchange with AI-powered strategy execution, risk control, and reporting.',
    downloads: 22600,
    stars: 1130,
    version: '1.0.0',
    installCommand: '> install the cryptocom-official-agent-trading skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/cryptocom-official-agent-trading',
    usageExample: '> auto trade on cdc\n> set risk limits\n> generate report',
    detailedDescription: 'Official Crypto.com autonomous agent trading integration enabling AI-driven strategy execution, configurable risk controls, automated rebalancing, and performance reporting on the Crypto.com Exchange.',
  },

  // ── Ethy Swap Agent ──
  {
    id: 'ethy-swap-agent',
    name: 'ethy-swap-agent',
    author: 'Ethy',
    authorHandle: '@ethy_io',
    tag: 'Skill',
    category: 'defi',
    description: 'Ethy DEX swap agent. Intelligent swap routing across ETH-native DEXes with MEV protection, slippage optimization, and gas cost forecasting.',
    downloads: 9800,
    stars: 490,
    version: '1.0.0',
    installCommand: '> install the ethy-swap-agent skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/ethy-swap-agent',
    usageExample: '> find best swap route\n> swap with MEV guard\n> estimate gas cost',
    detailedDescription: 'Ethy intelligent DEX swap agent aggregating Uniswap, Curve, and Balancer routes for optimal swaps with MEV protection, smart slippage management, and real-time gas cost forecasting.',
  },

  // ── Gate Assets ──
  {
    id: 'gate-assets',
    name: 'gate-assets',
    author: 'Gate.io',
    authorHandle: '@gate_io',
    tag: 'Skill',
    category: 'tools',
    description: 'Gate.io asset manager. Comprehensive wallet and asset management across Gate.io spot, futures, earn, and Web3 wallet with unified portfolio view.',
    downloads: 14800,
    stars: 740,
    version: '1.0.0',
    installCommand: '> install the gate-assets skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/gate-assets',
    usageExample: '> view gate portfolio\n> transfer assets\n> move to earn',
    detailedDescription: 'Gate.io asset management skill providing unified portfolio views across all Gate.io account types (spot, futures, earn, Web3 wallet), inter-account transfers, and PnL tracking.',
  },

  // ── Gate Coin Analysis ──
  {
    id: 'gate-coin-analysis',
    name: 'gate-coin-analysis',
    author: 'Gate.io',
    authorHandle: '@gate_io',
    tag: 'Skill',
    category: 'analytics',
    description: 'Gate.io token deep-dive analytics. Fundamental analysis, tokenomics breakdown, vesting schedules, and on-chain metrics for Gate-listed tokens.',
    downloads: 16400,
    stars: 820,
    version: '1.0.0',
    installCommand: '> install the gate-coin-analysis skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/gate-coin-analysis',
    usageExample: '> analyze token fundamentals\n> check vesting schedule\n> tokenomics breakdown',
    detailedDescription: 'Gate.io token analysis service providing fundamental research, tokenomics breakdowns, vesting cliff/unlock schedules, on-chain metrics, and comparative analysis for Gate-listed assets.',
  },

  // ── Gate DEX Market ──
  {
    id: 'gate-dex-market',
    name: 'gate-dex-market',
    author: 'Gate.io',
    authorHandle: '@gate_io',
    tag: 'Skill',
    category: 'analytics',
    description: 'Gate DEX market data. Real-time DEX liquidity pools, token prices, volume trends, and new pool alerts from Gate\'s Web3 DEX aggregator.',
    downloads: 12800,
    stars: 640,
    version: '1.0.0',
    installCommand: '> install the gate-dex-market skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/gate-dex-market',
    usageExample: '> check dex prices\n> find new pools\n> monitor liquidity',
    detailedDescription: 'Gate DEX market data aggregator covering real-time liquidity pool stats, token prices across DEXes, volume trend analysis, and new pool launch alerts from Gate\'s multichain DEX platform.',
  },

  // ── Gate DEX Trade ──
  {
    id: 'gate-dex-trade',
    name: 'gate-dex-trade',
    author: 'Gate.io',
    authorHandle: '@gate_io',
    tag: 'Skill',
    category: 'trading',
    description: 'Gate DEX trading skill. Execute on-chain swaps via Gate\'s DEX aggregator with best-price routing across multiple DEXes and chains.',
    downloads: 14200,
    stars: 710,
    version: '1.0.0',
    installCommand: '> install the gate-dex-trade skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/gate-dex-trade',
    usageExample: '> swap on gate dex\n> route best price\n> cross-chain swap',
    detailedDescription: 'Gate DEX trading integration executing on-chain swaps via Gate\'s aggregator with best-price routing across Uniswap, PancakeSwap, and other DEXes, plus native cross-chain swap support.',
  },

  // ── Gate DEX Wallet ──
  {
    id: 'gate-dex-wallet',
    name: 'gate-dex-wallet',
    author: 'Gate.io',
    authorHandle: '@gate_io',
    tag: 'Skill',
    category: 'tools',
    description: 'Gate Web3 wallet skill. Self-custody multichain wallet integrated with Gate DEX, NFT marketplace, and DApp browser for seamless Web3 access.',
    downloads: 16400,
    stars: 820,
    version: '1.0.0',
    installCommand: '> install the gate-dex-wallet skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/gate-dex-wallet',
    usageExample: '> access gate web3 wallet\n> browse nft marketplace\n> connect to dapp',
    detailedDescription: 'Gate Web3 wallet integration providing self-custody multichain asset management, seamless Gate DEX trading, NFT marketplace access, and DApp browser connectivity across 70+ networks.',
  },

  // ── Gate Market Overview ──
  {
    id: 'gate-market-overview',
    name: 'gate-market-overview',
    author: 'Gate.io',
    authorHandle: '@gate_io',
    tag: 'Skill',
    category: 'analytics',
    description: 'Gate.io market overview dashboard. Comprehensive market snapshot including top gainers/losers, volume leaders, new listings, and sector performance.',
    downloads: 24600,
    stars: 1230,
    version: '1.0.0',
    installCommand: '> install the gate-market-overview skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/gate-market-overview',
    usageExample: '> get market overview\n> top gainers today\n> sector performance',
    detailedDescription: 'Gate.io market overview providing top-gainers/losers, volume leaders, new listing highlights, sector rotation data, and global market cap metrics in a unified dashboard format.',
  },

  // ── Gate MCP Installer ──
  {
    id: 'gate-mcp-installer',
    name: 'gate-mcp-installer',
    author: 'Gate.io',
    authorHandle: '@gate_io',
    tag: 'Skill',
    category: 'tools',
    description: 'Gate.io MCP installer helper. Streamlined setup tool for installing and configuring the Gate.io MCP server with API key management and health checks.',
    downloads: 8600,
    stars: 430,
    version: '1.0.0',
    installCommand: '> install the gate-mcp-installer skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/gate-mcp-installer',
    usageExample: '> install gate mcp\n> configure api keys\n> run health check',
    detailedDescription: 'Gate.io MCP server installation helper providing step-by-step setup guidance, API key configuration, permission verification, and connectivity health checks for the Gate MCP integration.',
  },

  // ── Gate News Briefing ──
  {
    id: 'gate-news-briefing',
    name: 'gate-news-briefing',
    author: 'Gate.io',
    authorHandle: '@gate_io',
    tag: 'Skill',
    category: 'analytics',
    description: 'Gate.io crypto news briefing. Curated news with impact analysis, token-specific alerts, and AI-powered market implication summaries.',
    downloads: 19800,
    stars: 990,
    version: '1.0.0',
    installCommand: '> install the gate-news-briefing skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/gate-news-briefing',
    usageExample: '> daily news briefing\n> token-specific alerts\n> market implications',
    detailedDescription: 'Gate.io AI news briefing service curating crypto news with market impact analysis, token-specific alert filtering, and AI-generated summaries of trading implications for major events.',
  },

  // ── Gate Risk Check ──
  {
    id: 'gate-risk-check',
    name: 'gate-risk-check',
    author: 'Gate.io',
    authorHandle: '@gate_io',
    tag: 'Skill',
    category: 'analytics',
    description: 'Gate.io risk assessment tool. Portfolio risk scoring, token risk ratings, exchange safety metrics, and AI-powered risk warnings for Gate users.',
    downloads: 14800,
    stars: 740,
    version: '1.0.0',
    installCommand: '> install the gate-risk-check skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/gate-risk-check',
    usageExample: '> check portfolio risk\n> rate token risk\n> safety metrics',
    detailedDescription: 'Gate.io risk management tool providing portfolio-level risk scoring, individual token risk ratings based on liquidity/volatility/smart contract risks, and proactive risk warnings for open positions.',
  },

  // ── Gate Staking ──
  {
    id: 'gate-staking',
    name: 'gate-staking',
    author: 'Gate.io',
    authorHandle: '@gate_io',
    tag: 'Skill',
    category: 'defi',
    description: 'Gate.io staking and earn skill. Stake PoS tokens, participate in launchpad, and earn flexible/fixed APY yields across Gate.io Earn products.',
    downloads: 22400,
    stars: 1120,
    version: '1.0.0',
    installCommand: '> install the gate-staking skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/gate-staking',
    usageExample: '> stake on gate\n> join launchpad\n> compare earn APY',
    detailedDescription: 'Gate.io staking and earn integration for PoS token staking, launchpad participation, structured products, and flexible/fixed-term savings with APY comparison and auto-compound options.',
  },

  // ── Gate Trend Analysis ──
  {
    id: 'gate-trend-analysis',
    name: 'gate-trend-analysis',
    author: 'Gate.io',
    authorHandle: '@gate_io',
    tag: 'Skill',
    category: 'analytics',
    description: 'Gate.io market trend analysis. Identify emerging narratives, sector rotation, momentum shifts, and early-stage trend setups across Gate-listed assets.',
    downloads: 18600,
    stars: 930,
    version: '1.0.0',
    installCommand: '> install the gate-trend-analysis skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/gate-trend-analysis',
    usageExample: '> find emerging narratives\n> sector rotation\n> momentum shifts',
    detailedDescription: 'Gate.io trend analysis engine identifying emerging crypto narratives, sector rotation patterns, momentum shifts, and early-stage trend setups using on-chain data, social metrics, and price action.',
    trending: true,
  },

  // ── Hyperliquid CLI ──
  {
    id: 'hyperliquid-cli',
    name: 'hyperliquid-cli',
    author: 'Hyperliquid',
    authorHandle: '@hyperliquid_x',
    tag: 'Skill',
    category: 'tools',
    description: 'Hyperliquid CLI skill. Command-line interface for managing Hyperliquid positions, vaults, and API keys without a browser wallet.',
    downloads: 16400,
    stars: 820,
    version: '1.0.0',
    installCommand: '> install the hyperliquid-cli skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/hyperliquid-cli',
    usageExample: '> hl place order\n> hl view positions\n> hl deposit vault',
    detailedDescription: 'Hyperliquid command-line interface for server-side and scripted Hyperliquid operations including order placement, position management, vault deposits, and API key rotation without requiring a browser wallet.',
  },

  // ── Hyperliquid DEX ──
  {
    id: 'hyperliquid-dex',
    name: 'hyperliquid-dex',
    author: 'Hyperliquid',
    authorHandle: '@hyperliquid_x',
    tag: 'Skill',
    category: 'trading',
    description: 'Hyperliquid DEX full integration. Complete on-chain order book DEX access with spot, perps, vaults, and on-chain governance participation.',
    downloads: 38600,
    stars: 1930,
    version: '1.0.0',
    installCommand: '> install the hyperliquid-dex skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/hyperliquid-dex',
    usageExample: '> trade on HL dex\n> deposit to vault\n> vote governance',
    detailedDescription: 'Hyperliquid DEX complete integration covering spot and perpetuals trading on the on-chain order book, vault deposit/withdrawal, copy-trading, and HYPE governance voting.',
    featured: true,
  },

  // ── Hyperliquid MCP Server ──
  {
    id: 'hyperliquid-mcp-server',
    name: 'hyperliquid-mcp-server',
    author: 'Hyperliquid',
    authorHandle: '@hyperliquid_x',
    tag: 'Skill',
    category: 'trading',
    description: 'Hyperliquid MCP server. Full Hyperliquid DEX access via Model Context Protocol for AI agents — trade, query, and monitor on-chain.',
    downloads: 28400,
    stars: 1420,
    version: '1.0.0',
    installCommand: '> install the hyperliquid-mcp-server skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/hyperliquid-mcp-server',
    usageExample: '> connect hyperliquid MCP\n> query open orders\n> execute perp trade',
    detailedDescription: 'Official Hyperliquid MCP server exposing the complete Hyperliquid API through standardized MCP interfaces for AI agent trading, analytics, vault management, and governance actions.',
    trending: true,
  },

  // ── Hyperliquid Prime ──
  {
    id: 'hyperliquid-prime',
    name: 'hyperliquid-prime',
    author: 'Hyperliquid',
    authorHandle: '@hyperliquid_x',
    tag: 'Skill',
    category: 'trading',
    description: 'Hyperliquid Prime institutional tier. Enhanced API limits, dedicated websocket feeds, priority order routing, and institutional analytics for Hyperliquid.',
    downloads: 16400,
    stars: 820,
    version: '1.0.0',
    installCommand: '> install the hyperliquid-prime skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/hyperliquid-prime',
    usageExample: '> access HL Prime\n> priority order routing\n> institutional analytics',
    detailedDescription: 'Hyperliquid Prime institutional access tier with enhanced API rate limits, dedicated WebSocket data feeds, priority order routing, advanced analytics dashboard, and direct support for market makers.',
  },

  // ── Hyperliquid Trading ──
  {
    id: 'hyperliquid-trading',
    name: 'hyperliquid-trading',
    author: 'Hyperliquid',
    authorHandle: '@hyperliquid_x',
    tag: 'Skill',
    category: 'trading',
    description: 'Hyperliquid active trading toolkit. Advanced order types, position management, PnL analytics, and automated strategy execution on Hyperliquid.',
    downloads: 44800,
    stars: 2240,
    version: '1.0.0',
    installCommand: '> install the hyperliquid-trading skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/hyperliquid-trading',
    usageExample: '> execute HL strategy\n> view PnL analytics\n> manage open positions',
    detailedDescription: 'Hyperliquid active trading toolkit with advanced order management (limit, market, TWAP, scaled), real-time PnL analytics, position management, and automated strategy execution via the Hyperliquid SDK.',
  },

  // ── Kraken Official Alert Patterns ──
  {
    id: 'kraken-official-alert-patterns',
    name: 'kraken-official-alert-patterns',
    author: 'Kraken',
    authorHandle: '@kraken',
    tag: 'Skill',
    category: 'analytics',
    description: 'Kraken chart pattern alert system. Automated detection and alerts for head-and-shoulders, double tops/bottoms, triangles, and breakouts on Kraken.',
    downloads: 18600,
    stars: 930,
    version: '1.0.0',
    installCommand: '> install the kraken-official-alert-patterns skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/kraken-official-alert-patterns',
    usageExample: '> scan for H&S pattern\n> alert on breakout\n> detect double top',
    detailedDescription: 'Kraken official chart pattern recognition system detecting H&S, double tops/bottoms, pennants, flags, and channel breakouts with configurable alert thresholds and notification channels.',
  },

  // ── Kraken Official Autonomy Levels ──
  {
    id: 'kraken-official-autonomy-levels',
    name: 'kraken-official-autonomy-levels',
    author: 'Kraken',
    authorHandle: '@kraken',
    tag: 'Skill',
    category: 'tools',
    description: 'Kraken AI autonomy level manager. Configure and enforce trading autonomy tiers — from fully manual to fully autonomous — for AI trading agents on Kraken.',
    downloads: 12400,
    stars: 620,
    version: '1.0.0',
    installCommand: '> install the kraken-official-autonomy-levels skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/kraken-official-autonomy-levels',
    usageExample: '> set autonomy level\n> configure approval gates\n> review ai decisions',
    detailedDescription: 'Kraken autonomy level framework for AI trading agents. Configure human-in-the-loop approval gates, fully-autonomous execution tiers, and decision audit logs for compliant AI trading workflows.',
  },

  // ── Kraken Official Basis Trading ──
  {
    id: 'kraken-official-basis-trading',
    name: 'kraken-official-basis-trading',
    author: 'Kraken',
    authorHandle: '@kraken',
    tag: 'Skill',
    category: 'trading',
    description: 'Kraken basis trading skill. Cash-and-carry arbitrage between Kraken spot and futures, monitoring basis spreads and execution windows.',
    downloads: 14800,
    stars: 740,
    version: '1.0.0',
    installCommand: '> install the kraken-official-basis-trading skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/kraken-official-basis-trading',
    usageExample: '> find basis opportunity\n> execute cash-carry arb\n> monitor spread',
    detailedDescription: 'Kraken basis trading integration for cash-and-carry arbitrage between spot and futures markets. Monitor basis spreads, identify profitable carry windows, and execute coordinated spot-futures positions.',
  },

  // ── Kraken Official Error Recovery ──
  {
    id: 'kraken-official-error-recovery',
    name: 'kraken-official-error-recovery',
    author: 'Kraken',
    authorHandle: '@kraken',
    tag: 'Skill',
    category: 'tools',
    description: 'Kraken error recovery framework. Automated detection and recovery for failed orders, API errors, connectivity issues, and position reconciliation.',
    downloads: 9800,
    stars: 490,
    version: '1.0.0',
    installCommand: '> install the kraken-official-error-recovery skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/kraken-official-error-recovery',
    usageExample: '> recover failed order\n> reconnect on error\n> reconcile positions',
    detailedDescription: 'Kraken automated error recovery framework handling order failure retries, API rate limit backoff, connection drop reconnection, and position reconciliation after unexpected disconnections.',
  },

  // ── Kraken Official Fee Optimization ──
  {
    id: 'kraken-official-fee-optimization',
    name: 'kraken-official-fee-optimization',
    author: 'Kraken',
    authorHandle: '@kraken',
    tag: 'Skill',
    category: 'tools',
    description: 'Kraken fee optimization skill. Analyze and minimize trading fees through maker/taker optimization, volume tiers, and pro order type selection.',
    downloads: 14200,
    stars: 710,
    version: '1.0.0',
    installCommand: '> install the kraken-official-fee-optimization skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/kraken-official-fee-optimization',
    usageExample: '> analyze trading fees\n> optimize maker orders\n> check fee tier',
    detailedDescription: 'Kraken fee optimization tool analyzing historical fee costs, recommending order type switches (maker vs taker), tracking volume tier progression, and projecting fee savings from strategy adjustments.',
  },

  // ── Kraken Official Funding Carry ──
  {
    id: 'kraken-official-funding-carry',
    name: 'kraken-official-funding-carry',
    author: 'Kraken',
    authorHandle: '@kraken',
    tag: 'Skill',
    category: 'trading',
    description: 'Kraken funding rate carry strategy. Harvest positive funding rates on Kraken futures by maintaining delta-neutral positions and optimizing roll timing.',
    downloads: 16400,
    stars: 820,
    version: '1.0.0',
    installCommand: '> install the kraken-official-funding-carry skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/kraken-official-funding-carry',
    usageExample: '> start carry strategy\n> optimize roll timing\n> track carry yield',
    detailedDescription: 'Kraken funding rate carry strategy execution — maintain delta-neutral spot/futures positions to harvest perpetual funding income, with optimal roll timing, collateral management, and carry yield tracking.',
  },

  // ── Kraken Official Funding Ops ──
  {
    id: 'kraken-official-funding-ops',
    name: 'kraken-official-funding-ops',
    author: 'Kraken',
    authorHandle: '@kraken',
    tag: 'Skill',
    category: 'tools',
    description: 'Kraken funding operations skill. Manage deposits, withdrawals, and cross-account funding workflows on Kraken with compliance and audit trail support.',
    downloads: 12800,
    stars: 640,
    version: '1.0.0',
    installCommand: '> install the kraken-official-funding-ops skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/kraken-official-funding-ops',
    usageExample: '> initiate withdrawal\n> transfer between accounts\n> generate audit trail',
    detailedDescription: 'Kraken funding operations management for institutional users covering multi-account deposits and withdrawals, inter-account transfers, whitelist address management, and compliance audit trail generation.',
  },

  // ── Kraken Official Futures Risk ──
  {
    id: 'kraken-official-futures-risk',
    name: 'kraken-official-futures-risk',
    author: 'Kraken',
    authorHandle: '@kraken',
    tag: 'Skill',
    category: 'analytics',
    description: 'Kraken futures risk management. Real-time margin monitoring, liquidation price tracking, VaR calculation, and automated deleveraging for Kraken Futures.',
    downloads: 18600,
    stars: 930,
    version: '1.0.0',
    installCommand: '> install the kraken-official-futures-risk skill from https://github.com/jiayaoqijia/cryptoskill/tree/main/skills/exchanges/kraken-official-futures-risk',
    usageExample: '> monitor margin ratio\n> check liquidation price\n> calculate VaR',
    detailedDescription: 'Kraken Futures real-time risk management covering margin ratio monitoring, liquidation price calculations, portfolio Value-at-Risk (VaR) estimation, and automated position reduction triggers to prevent liquidations.',
  },
];